"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { apiGet } from "@/lib/api";

export interface IndexQuote {
  /** Live last (cash index in RTH, E-mini futures proxy after hours). */
  latest: number;
  /** Cash market-close reference for gap / green-red. */
  prev: number | null;
  /** Symbol actually quoted (`$SPX` or `/ESU26`). */
  quoteSymbol?: string;
}

export type IndicesMode = "cash" | "futures";

/** Indices shown in the desktop left pane and the mobile home strip. */
export const MARKET_INDICES: { symbol: string; label: string }[] = [
  { symbol: "$DJI", label: "DJIA" },
  { symbol: "$NDX", label: "NDX" },
  { symbol: "$SPX", label: "SPX" },
  { symbol: "$VIX", label: "VIX" },
  { symbol: "$RUT", label: "RUT" },
  { symbol: "$RVX", label: "RVX" },
];

interface StreamQuote {
  symbol: string;
  quote_symbol?: string;
  last_price: number;
  /** Cash market-close reference (backend sets this even in futures mode). */
  close_price: number;
}

interface StreamPayload {
  mode: IndicesMode;
  quotes: StreamQuote[];
  /** Agent Schwab account socket (fills/cancels). Not this quote stream. */
  wss_active?: boolean;
  /** Index LEVELONE is down; lasts are from the 5s quotes HTTP poll. */
  polling?: boolean;
  /** Why nothing live feeds the rows (e.g. Schwab login expired); null when live. */
  error?: string | null;
}

export interface IndexGap {
  latest: number;
  close: number;
  chg: number;
  pct: number;
}

/** Gap of live last vs cash market close (points + %). */
export function indexGap(d: IndexQuote | undefined): IndexGap | null {
  if (!d || d.prev == null || !(d.prev > 0) || !(d.latest > 0)) return null;
  const chg = d.latest - d.prev;
  return { latest: d.latest, close: d.prev, chg, pct: (chg / d.prev) * 100 };
}

/** Green if last is above market close, red otherwise (including flat). */
export function indexChangeColor(latest: number, close: number | null | undefined): string {
  if (close == null || !(close > 0)) return "text-muted";
  return latest > close ? "text-green" : "text-red";
}

/** Coarse band for tooltips — answers “a little / a lot”. */
export function gapSizeWord(pct: number): string {
  const a = Math.abs(pct);
  if (a < 0.15) return "about flat";
  if (a < 0.35) return "a little";
  if (a < 0.75) return "moderately";
  if (a < 1.25) return "solidly";
  return "a lot";
}

/** Tooltip for one index row. */
export function indexGapTitle(label: string, mode: IndicesMode, d: IndexQuote | undefined): string {
  const g = indexGap(d);
  if (!g) return label;
  const dir = g.chg > 0 ? "up" : g.chg < 0 ? "down" : "unchanged";
  const size = g.chg === 0 ? "" : ` ${gapSizeWord(g.pct)}`;
  if (mode === "futures") {
    const via = d?.quoteSymbol ? ` (${d.quoteSymbol})` : " (futures)";
    return `${label}${via}: ${g.latest.toFixed(2)} vs last cash session close ${g.close.toFixed(2)} — ${dir}${size} ${g.pct >= 0 ? "+" : ""}${g.pct.toFixed(2)}% (after 4pm = today's close; after midnight = prior session)`;
  }
  return `${label}: ${g.latest.toFixed(2)} vs prior close ${g.close.toFixed(2)} — ${dir}${size} ${g.pct >= 0 ? "+" : ""}${g.pct.toFixed(2)}%`;
}

/**
 * Live index quotes for desktop + mobile.
 *
 * - Cash RTH (9:30–16:00 ET trading day): live cash lasts — Schwab LEVELONE,
 *   or tastytrade DxLink when `?account=` is a tastytrade account.
 * - Otherwise: the front-month futures contract (`/ESU26`, rolling to the next
 *   contract when it stops trading; RVX stays cash — no future), still labeled
 *   as the cash names.
 * - Gap / color vs the most recent *completed* cash session close (today after
 *   16:00 ET; prior trading day once the calendar rolls past midnight).
 *
 * One WebSocket per tab: every hook instance (left pane, strategy page,
 * mobile strip) subscribes to a module-level store, and the store only
 * publishes when a value actually changed, so an unchanged tick causes no
 * re-render anywhere.
 */
interface IndicesStore {
  marketData: Record<string, IndexQuote>;
  wsConnected: boolean;
  wssActive: boolean;
  polling: boolean;
  mode: IndicesMode;
  /** Backend's reason the rows are not live (shown in the header dot tooltip). */
  error: string | null;
}

let store: IndicesStore = {
  marketData: {},
  wsConnected: false,
  wssActive: false,
  polling: true,
  mode: "cash",
  error: null,
};
const listeners = new Set<() => void>();
let ws: WebSocket | null = null;
let wsKey: string | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
// HTTP fallback while the socket is down (some office networks drop websocket
// upgrades): poll the same payload as a plain GET every few seconds.
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollInFlight = false;
const POLL_MS = 5000;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let refCount = 0;
let lastMode: IndicesMode | null = null;
let seededFor: string | null = null;

function sameQuote(a: IndexQuote | undefined, b: IndexQuote | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.latest === b.latest && a.prev === b.prev && a.quoteSymbol === b.quoteSymbol;
}

function sameMarketData(a: Record<string, IndexQuote>, b: Record<string, IndexQuote>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => sameQuote(a[k], b[k]));
}

function publish(patch: Partial<IndicesStore>) {
  const next = { ...store, ...patch };
  if (
    next.wsConnected === store.wsConnected
    && next.wssActive === store.wssActive
    && next.polling === store.polling
    && next.mode === store.mode
    && next.error === store.error
    && sameMarketData(next.marketData, store.marketData)
  ) return;
  store = next;
  for (const l of listeners) l();
}

/** Seed cash market closes from history once per user. Merge so a faster WS tick isn't wiped. */
function seedCloses(user: string) {
  if (seededFor === user) return;
  seededFor = user;
  Promise.all(
    MARKET_INDICES.map(({ symbol }) =>
      apiGet<{ rows: { close: number }[] }>(`/prices/${encodeURIComponent(symbol)}?limit=1`)
        .then((data) => ({ symbol, rows: data.rows }))
        .catch(() => ({ symbol, rows: [] as { close: number }[] })),
    ),
  ).then((results) => {
    const map: Record<string, IndexQuote> = { ...store.marketData };
    for (const r of results) {
      if (r.rows.length === 0) continue;
      const cashClose = r.rows[0].close;
      if (!(cashClose > 0)) continue;
      const ex = store.marketData[r.symbol];
      map[r.symbol] = {
        latest: ex && ex.latest > 0 ? ex.latest : cashClose,
        prev: cashClose,
        quoteSymbol: ex?.quoteSymbol,
      };
    }
    publish({ marketData: map });
  });
}

/** Fold one stream payload (socket frame or HTTP snapshot) into the store. */
function applyPayload(raw: StreamPayload | StreamQuote[], viaSocket: boolean) {
  let nextMode: IndicesMode = "cash";
  let quotes: StreamQuote[];
  if (Array.isArray(raw)) {
    quotes = raw;
  } else if (raw && Array.isArray(raw.quotes)) {
    quotes = raw.quotes;
    nextMode = raw.mode === "futures" ? "futures" : "cash";
  } else {
    return;
  }

  if (viaSocket && lastMode === "cash" && nextMode === "futures" && ws?.readyState === WebSocket.OPEN) {
    ws.send("save");
  }
  lastMode = nextMode;

  const patch: Partial<IndicesStore> = { mode: nextMode };
  if (!Array.isArray(raw)) {
    if (typeof raw.wss_active === "boolean") patch.wssActive = raw.wss_active;
    // A polled snapshot is by definition not the live stream.
    patch.polling = !viaSocket || raw.polling === true;
    patch.error = typeof raw.error === "string" && raw.error ? raw.error : null;
  }

  const prev = store.marketData;
  const map: Record<string, IndexQuote> = { ...prev };
  for (const q of quotes) {
    if (!(q.last_price > 0)) continue;
    const ex = prev[q.symbol];
    const refClose =
      q.close_price > 0 ? q.close_price : ex?.prev != null && ex.prev > 0 ? ex.prev : null;
    map[q.symbol] = { latest: q.last_price, prev: refClose, quoteSymbol: q.quote_symbol };
  }
  patch.marketData = map;
  publish(patch);
}

function onMessage(event: MessageEvent) {
  let raw: StreamPayload | StreamQuote[];
  try {
    raw = JSON.parse(event.data);
  } catch {
    return; // not JSON: ignore
  }
  // Deliberately NOT wrapped: a React error thrown while applying a tick
  // (e.g. "Maximum update depth exceeded") must surface, not silently freeze
  // the pane while the socket stays green.
  applyPayload(raw, true);
}

/** `ws(s)://host/api/stream/indices?...` → `/stream/indices-snapshot?...` for apiGet. */
function snapshotPath(): string | null {
  if (!wsKey) return null;
  const i = wsKey.indexOf("/api/stream/indices");
  if (i < 0) return null;
  return `/stream/indices-snapshot${wsKey.slice(i + "/api/stream/indices".length)}`;
}

async function pollOnce() {
  if (pollInFlight || ws?.readyState === WebSocket.OPEN) return;
  const path = snapshotPath();
  if (!path) return;
  pollInFlight = true;
  try {
    applyPayload(await apiGet<StreamPayload>(path), false);
  } catch { /* try again next tick */ } finally {
    pollInFlight = false;
  }
}

function startPolling() {
  if (pollTimer) return;
  pollOnce();
  pollTimer = setInterval(() => { if (!document.hidden) pollOnce(); }, POLL_MS);
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function connect() {
  if (ws || !wsKey) return;
  const sock = new WebSocket(wsKey);
  ws = sock;
  // A blocked upgrade can hang for a long time before it closes; don't leave
  // the pane blank meanwhile.
  setTimeout(() => { if (ws === sock && sock.readyState !== WebSocket.OPEN) startPolling(); }, 3000);
  sock.onopen = () => { stopPolling(); publish({ wsConnected: true }); };
  sock.onmessage = onMessage;
  sock.onclose = () => {
    if (ws === sock) ws = null;
    publish({ wsConnected: false, polling: true });
    if (wsKey) {
      startPolling();
      if (!reconnectTimer) {
        reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 5000);
      }
    }
  };
  sock.onerror = () => sock.close();
}

function teardown() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  stopPolling();
  if (ws) { ws.onclose = null; ws.close(); ws = null; }
  wsKey = null;
  lastMode = null;
  publish({ wsConnected: false, wssActive: false, polling: true, error: null });
}

function acquire(url: string) {
  refCount += 1;
  if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; }
  if (wsKey !== url) {
    teardown();
    wsKey = url;
    connect();
  }
}

function release() {
  refCount -= 1;
  if (refCount > 0 || releaseTimer) return;
  // Short grace so a dev Strict Mode remount (or a route change that keeps
  // the left pane) reuses the open socket instead of reconnecting.
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    if (refCount === 0) teardown();
  }, 250);
}

export function useMarketIndices() {
  const { user } = useAuth();
  const account = useSearchParams().get("account") ?? "";
  // Plain state, NOT useSyncExternalStore. A store tick through uSES renders
  // at sync priority, which runs the page's effects inside the commit; their
  // setState (spot → vsLive) leaves work pending, and React counts every such
  // commit as a nested update. A busy tape outruns the heavy strategy-page
  // render, the count never resets, and the 51st tick throws "Maximum update
  // depth exceeded" from whichever setState happens next.
  const [snap, setSnap] = useState(store);
  useEffect(() => {
    const l = () => setSnap(store);
    listeners.add(l);
    l(); // catch a tick that landed between render and subscribe
    return () => { listeners.delete(l); };
  }, []);

  useEffect(() => {
    if (!user) return;
    seedCloses(user);
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const qs = account ? `?account=${encodeURIComponent(account)}` : "";
    acquire(`${protocol}//${window.location.host}/api/stream/indices${qs}`);
    return release;
  }, [user, account]);

  return snap;
}
